import { useCallback, useEffect, useState } from "react";

import ArtifactClient from "../backend/clients/ArtifactClient";
import PlaceClient from "../backend/clients/PlaceClient";
import { useToolContext } from "../domain/ToolContext/ToolContext";
import {
  ArtifactFileUploadStatus,
  ArtifactType,
  Place,
  PlaceDesignData,
} from "../models";
import AggregatedLogger from "../observability/AggregatedLogger/AggregatedLogger";
import normalizeError from "../utils/errors";

export default function usePlaceDesignData(pageSize: number = 100) {
  const context = useToolContext();

  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PlaceDesignData[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setData([]);
    setError(null);

    try {
      const places = new Map<string, Place>();
      let continuationToken = "";
      const placeDesignData: PlaceDesignData[] = [];
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await PlaceClient.getPlaces(
          context.token,
          continuationToken,
          pageSize,
        );
        continuationToken = response.continuationToken;

        response.items.forEach((place) => places.set(place.id, place));
      } while (continuationToken);

      await Promise.allSettled(
        Array.from(places.values()).map(async (place) => {
          try {
            const artifacts = await ArtifactClient.getArtifacts(
              context.token,
              place.id,
            );

            const validArtifacts = artifacts.filter(
              ({ artifactType, fileUploadStatus }) =>
                artifactType === ArtifactType.ProjectDatabase &&
                fileUploadStatus === ArtifactFileUploadStatus.FileUploaded,
            );

            if (validArtifacts.length > 0) {
              placeDesignData.push({ place, artifacts: validArtifacts });
            }
          } catch (error) {
            AggregatedLogger.error(
              "usePlaceDesignData: fetching artifact data:",
              error,
            );
            setError(
              normalizeError(error, new Error("Failed to load artifact data")),
            );
          }
        }),
      );

      const sortByDate = (a: PlaceDesignData, b: PlaceDesignData) =>
        new Date(b.artifacts[0].modifiedDate).getTime() -
        new Date(a.artifacts[0].modifiedDate).getTime();

      placeDesignData.sort(sortByDate);

      setData(placeDesignData);
    } catch (error) {
      AggregatedLogger.error("usePlaceDesignData: fetching place data:", error);
      setError(normalizeError(error, new Error("Failed to load place data")));
    }

    setLoading(false);
  }, [context.token, pageSize]);

  useEffect(() => {
    if (!context.isOffline) {
      loadData();
    } else {
      setLoading(false);
      setData([]);
      setError(null);
    }
  }, [loadData, context.isOffline]);

  return {
    data,
    error,
    loading,
  };
}
